import React, {PureComponent, Fragment} from 'react';
import PropTypes from 'prop-types';
import ChevronRight from 'react-feather/dist/icons/chevron-right.js';
import ChevronLeft from 'react-feather/dist/icons/chevron-left.js';
import keySpline from './KeySpline.js';

import './EoCarousel.scss';

const DIRECTION_WTF    = 0;
const DIRECTION_TOP    = 1;
const DIRECTION_RIGHT  = 2;
const DIRECTION_BOTTOM = 4;
const DIRECTION_LEFT   = 8;

function getDirection(x1, y1, x2, y2) {
  const xd = x2 - x1;
  const yd = y2 - y1;
  const angle = Math.atan2(xd, yd) * 180 / Math.PI;

  if (angle <= 45 && angle > -45) {
    return DIRECTION_TOP;
  } else if (angle <= -45 && angle > -135) {
    return DIRECTION_RIGHT;
  } else if ((angle <= -135 && angle >= -180) || (angle <= 180 && angle > 135)) {
    return DIRECTION_BOTTOM;
  } else if (angle <= 135 && angle > 45) {
    return DIRECTION_LEFT;
  }
  return DIRECTION_WTF;
}

function addEvent(elem, type, eventHandle) {
  if (elem.addEventListener) {
    elem.addEventListener(type, eventHandle, false);
  } else {
    elem['on' + type] = eventHandle;
  }
}

function removeEvent(elem, type, eventHandle) {
  if (elem.removeEventListener) {
    elem.removeEventListener(type, eventHandle, false);
  } else {
    elem['on' + type] = null;
  }
}

export default class EoCarousel extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      carouselIndex: 0,
      carouselLength: 0,
      carouselPages: [],
      containerWidth: 0,
      decalX: 0,
      realDecalX: 0
    };

    this.touchStart = {x: 0, y: 0};
    this.touchDirection = DIRECTION_WTF;

    this.getTime = keySpline(0, 0.5, 1, 1);
    this.chillCurve = (time, startValue, change, duration) => {
      time = time / duration;
      return change * this.getTime(time) + startValue;
    };
    this.carouselRef = React.createRef();
  }

  static defaultProps = {
    className: '',
    loop: false,
    withArrows: true
  };

  static propTypes = {
    className: PropTypes.string,
    loop: PropTypes.bool,
    withArrows: PropTypes.bool
  };

  componentDidUpdate(prevProps, prevState, prevContext) {
    if (this.state.carouselIndex !== prevState.carouselIndex) {
      this.goto(this.state.carouselIndex);
    }
    if (this.props.children.length !== prevProps.children.length) {
      this.onLengthChange(this.props.children.length);
    }
  }

  componentDidMount() {
    addEvent(window, 'resize', this.resizeSlides);
    addEvent(document, 'readystatechange', this.resizeSlides);
    addEvent(this.carouselRef.current, 'touchstart', this.onTouchStart);
    addEvent(this.carouselRef.current, 'touchend', this.onTouchEnd);
    addEvent(this.carouselRef.current, 'touchmove', this.onTouchMove);
    addEvent(this.carouselRef.current, 'touchcancel', this.onTouchCancel);

    this.onLengthChange(this.props.children.length);
    this.resizeSlides();
  }

  componentWillUnmount() {
    removeEvent(window, 'resize', this.resizeSlides);
    removeEvent(document, 'readystatechange', this.resizeSlides);
    removeEvent(this.carouselRef.current, 'touchstart', this.onTouchStart);
    removeEvent(this.carouselRef.current, 'touchend', this.onTouchEnd);
    removeEvent(this.carouselRef.current, 'touchmove', this.onTouchMove);
    removeEvent(this.carouselRef.current, 'touchcancel', this.onTouchCancel);
  }

  onTouchStart = (e) => {
    this.touchStart = {
      x: e.touches[0].pageX,
      y: e.touches[0].pageY
    };
    this.touchDirection = DIRECTION_WTF;
  };

  onTouchMove = (e) => {
    const currentPosition = {
      x: e.touches[0].pageX,
      y: e.touches[0].pageY
    };

    const direction = getDirection(
      currentPosition.x,
      currentPosition.y,
      this.touchStart.x,
      this.touchStart.y
    );

    if (direction !== DIRECTION_RIGHT && direction !== DIRECTION_LEFT) {
      return;
    }

    e.preventDefault();

    const realDecal = currentPosition.x - this.touchStart.x;
    let decal = realDecal;
    const currentTranslation = this.getCurrentTranslation();
    const maxDecal = this.state.containerWidth / 2;

    if (currentTranslation < this.state.containerWidth || currentTranslation > (this.state.containerWidth * this.props.children.length + 1)) {
      const inverseMe = (currentTranslation > (this.state.containerWidth * this.props.children.length + 1) ? -1 : 1);
      const currentTime = realDecal * inverseMe / maxDecal;
      decal = decal * inverseMe;
      if (currentTime >= 4) {
        decal = maxDecal;
      } else {
        decal = Math.round(this.chillCurve(currentTime, 0, maxDecal, 4));
      }
      decal = decal * inverseMe;
    }

    this.setState({
      decalX: decal,
      realDecalX: realDecal
    });
  };

  onTouchEnd = (e) => {
    const positiveMe = this.state.decalX < 0 ? -1 : 1;

    if (this.state.decalX * positiveMe >= 60) {
      if (positiveMe < 0) {
        this.next();
      } else {
        this.prev();
      }
    }
    this.onTouchCancel();
  };

  onTouchCancel = (e) => {
    this.setState({decalX: 0, realDecalX: 0});
  };

  resizeSlides = () => {
    this.setState({
      containerWidth: this.carouselRef.current.clientWidth
    });
  };

  goto = (index) => {
    let newIndex = index;
    if (index >= this.props.children.length) {
      newIndex = (this.props.loop ? 0 : this.props.children.length - 1);
    } else if (index < 0) {
      newIndex = (this.props.loop ? this.props.children.length - 1 : 0);
    }

    this.setState({carouselIndex: newIndex});
    this.onIndexChange(newIndex);
  };

  next = () => {
    this.goto(this.state.carouselIndex + 1);
  };

  prev = () => {
    return this.goto(this.state.carouselIndex - 1);
  };

  getCurrentTranslation = (withDecal = true) => {
    return (this.state.carouselIndex + 1) * this.state.containerWidth - (withDecal ? this.state.decalX : 0);
  };

  getSliderStyle = () => {
    const styles = {
      transform: `translateX(-${this.getCurrentTranslation()}px)`
    };
    if (this.state.decalX !== 0) {
      styles.transitionDuration = '0s';
    }
    return styles;
  };

  sliderGoto = (page, e) => {
    e.preventDefault();
    this.setState({carouselIndex: page});
  };

  onIndexChange(index) {
    if (index === this.state.carouselIndex) {
      return;
    }
    this.setState({carouselIndex: index});
  }

  onLengthChange = (length) => {
    if (this.state.carouselLength === length) {
      return;
    }

    const pages = [];
    for (let i = 0; i < length; i++) {
      pages.push(i);
    }

    this.setState({
      carouselLength: length,
      carouselPages: pages
    });
  }

  render() {
    return (
      <Fragment>
        <div ref={this.carouselRef} className={['eo-carousel d-flex flex-wrap quotes', this.props.className || ''].join(' ')}>
          <div className="eo-carousel-slider" style={this.getSliderStyle()}>
            <div className="eo-carousel-slide placeholder-left" style={{width: this.state.containerWidth}}/>
            {this.props.children.map((child, i) =>
              <div key={i} style={{width: this.state.containerWidth}}
                className={['eo-carousel-slide', (i === this.state.carouselIndex ? 'focused' : '')].join(' ')}>
                {child}
              </div>
            )}
            <div className="eo-carousel-slide placeholder-right" style={{width: this.state.containerWidth}}/>
          </div>
          {this.props.withArrows ?
            <Fragment>
              <button
                className="control prev d-flex align-items-center justify-content-center"
                disabled={this.props.loop ? false : this.state.carouselIndex === 0}
                onClick={this.prev}
              >
                <ChevronLeft aria-hidden="true"/>
                <span className="sr-only">Précédent</span>
              </button>
              <button
                className="control next d-flex align-items-center justify-content-center"
                disabled={this.props.loop ? false : this.state.carouselIndex === this.state.carouselLength - 1}
                onClick={this.next}
              >
                <ChevronRight aria-hidden="true"/>
                <span className="sr-only">Suivant</span>
              </button>
            </Fragment> : ''
          }
        </div>
        <div className="eo-carousel-indicators d-flex mb-3">
          {this.state.carouselPages.map((page) => {
            return (
              <a key={page} onClick={(e) => this.sliderGoto(page, e)} href="#0"
                className={['bar-link', (page === this.state.carouselIndex ? 'active' : '')].join(' ')}>
                <span className="bar"/>
              </a>
            );
          })}
        </div>
      </Fragment>
    );
  }
}
